import { Injectable, signal } from '@angular/core';
import { error, isFirefoxBrowser, isSafariBrowser } from '@4players/odin-common';
import { UserActivationService } from './user-activation.service';
import { isAndroid, isTauri } from '../../shared/helpers/platform';

export type PermissionStateCombined = PermissionState | 'unknown';

/**
 * Browser permission state machine for camera and microphone, plus the
 * user-consent gates built on top of it (permission tutorial overlay, media
 * approval gate). Keeps the `cameraAccess`/`microphoneAccess` signals in sync
 * with the Permissions API — via `onchange` listeners on Chrome/Firefox and
 * polling on Safari — and encapsulates the per-browser quirks of requesting
 * access. Device enumeration and capture live elsewhere (DeviceService /
 * LocalStreamService); this service owns nothing but permission state.
 */
@Injectable({
  providedIn: 'root',
})
export class MediaPermissionsService {
  private _cameraAccess = signal<PermissionStateCombined>('unknown');
  cameraAccess = this._cameraAccess.asReadonly();
  private _microphoneAccess = signal<PermissionStateCombined>('unknown');
  microphoneAccess = this._microphoneAccess.asReadonly();

  /** Set to true when a permission denial should show the tutorial overlay. */
  private _showPermissionTutorial = signal(false);
  showPermissionTutorial = this._showPermissionTutorial.asReadonly();

  private _safariPollInterval: ReturnType<typeof setInterval> | null = null;

  // Media approval gate — blocks audio input creation until user explicitly opts in (e.g. preparation area)
  private _mediaApprovalResolve: (() => void) | null = null;
  private _mediaApprovalPromise: Promise<void> = Promise.resolve();

  constructor(private _userActivation: UserActivationService) {
    this.initPermissionListeners();
  }

  openPermissionTutorial() {
    this._showPermissionTutorial.set(true);
  }

  dismissPermissionTutorial() {
    this._showPermissionTutorial.set(false);
  }

  /** Sets a gate that blocks waitForMediaApproval() until approveMediaAccess() is called. */
  setMediaApprovalGate() {
    this._mediaApprovalPromise = new Promise((resolve) => {
      this._mediaApprovalResolve = resolve;
    });
  }

  /** Resolves the media approval gate and resets it for future checks. */
  approveMediaAccess() {
    this._mediaApprovalResolve?.();
    this._mediaApprovalResolve = null;
    this._mediaApprovalPromise = Promise.resolve();
  }

  /** Awaits media approval if a gate is active, resolves immediately otherwise. */
  waitForMediaApproval(): Promise<void> {
    return this._mediaApprovalPromise;
  }

  /**
   * Sets up reactive permission listeners.
   * - Chrome/Firefox: Uses PermissionStatus.onchange for instant updates.
   * - Safari: Falls back to polling since onchange doesn't fire.
   * - Firefox: 'denied' is never reported by the Permissions API, so denied
   *   detection still happens in requestMic()/requestCam() via requestUserMedia().
   */
  private async initPermissionListeners() {
    if (isTauri) {
      await this.readNativePermissions();
      return;
    }
    const usePolling = isSafariBrowser();

    if (usePolling) {
      // Safari: poll every 2s since onchange is not supported
      await this.pollPermissions(); // Initial read
      this._safariPollInterval = setInterval(() => this.pollPermissions(), 2000);
    } else {
      // Chrome/Firefox: use onchange events
      await this.watchPermission('microphone', (state) => this.updateMicrophoneAccess(state));
      await this.watchPermission('camera', (state) => this.updateCameraAccess(state));
    }
  }

  /**
   * Native app: the OS owns the permissions, not the WebView (whose
   * getUserMedia prompt would be a second, unrelated one). The camera state
   * comes from the plugin. Only Android has a microphone runtime permission
   * to query; iOS asks when the capture opens, desktop does not ask at all.
   */
  private async readNativePermissions() {
    this._microphoneAccess.set(isAndroid ? 'prompt' : 'granted');
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      this._cameraAccess.set(
        await invoke<PermissionState>('plugin:odin|video_device_permission_status')
      );
    } catch (e) {
      console.warn('failed to read the native camera permission', e);
    }
  }

  private async requestNativeMic(): Promise<PermissionStateCombined> {
    const { invoke } = await import('@tauri-apps/api/core');
    const granted = await invoke<boolean>('plugin:odin|request_microphone_permission').catch(
      () => false
    );
    await this.updateMicrophoneAccess(granted ? 'granted' : 'denied');
    return this._microphoneAccess();
  }

  private async requestNativeCam(): Promise<PermissionStateCombined> {
    const { requestCameraPermission } = await import('@4players/odin-plugin-tauri/capture');
    const state = await requestCameraPermission().catch((): PermissionState => 'denied');
    await this.updateCameraAccess(state);
    return state;
  }

  /**
   * Queries the permission and attaches an onchange listener to keep the signal in sync.
   */
  private async watchPermission(
    permission: 'camera' | 'microphone',
    onUpdate: (state: PermissionState) => void
  ) {
    try {
      const status = await navigator.permissions.query({ name: permission as PermissionName });
      onUpdate(status.state);
      status.onchange = () => onUpdate(status.state);
    } catch {
      // Permissions API not supported for this permission
    }
  }

  /**
   * Polls both permission states (Safari fallback).
   */
  private async pollPermissions() {
    try {
      const mic = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      this.updateMicrophoneAccess(mic.state);
    } catch {
      // microphone permission query not supported
    }
    try {
      const cam = await navigator.permissions.query({ name: 'camera' as PermissionName });
      this.updateCameraAccess(cam.state);
    } catch {
      // camera permission query not supported
    }
  }

  private async updateMicrophoneAccess(state: PermissionState) {
    const prev = this._microphoneAccess();
    this._microphoneAccess.set(state);
    if (state === 'denied' && prev !== 'denied' && this._userActivation.activated()) {
      this._showPermissionTutorial.set(true);
    }
  }

  private async updateCameraAccess(state: PermissionState) {
    const prev = this._cameraAccess();
    this._cameraAccess.set(state);
    if (state === 'denied' && prev !== 'denied' && this._userActivation.activated()) {
      this._showPermissionTutorial.set(true);
    }
  }

  /**
   * Prompt the user for Audio/Video permissions. On Firefox, it prevents the DeviceList from being
   * empty.
   *
   * If permission is "prompt": Prompting the user every time
   * If permission is "denied": No Prompt gets displayed - the user has to reset browser permissions
   * Note: Firefox seems to not having the state "denied" -permission is also "prompt" but getUserMedia()
   * returns an error.
   */
  async requestUserMedia(kind: 'audio' | 'video'): Promise<boolean> {
    await this._userActivation.waitForActivation();
    try {
      const ms = await navigator.mediaDevices.getUserMedia({
        [kind]: true,
      });
      ms?.getTracks().forEach((track) => {
        track.stop();
        ms.removeTrack(track);
      });
      return true;
    } catch (e) {
      console.warn(e);
      return false;
    }
  }

  /**
   * Prompt the user for Camera permissions.
   * The reactive listeners (onchange / poll) update the signal automatically.
   * On Firefox, we additionally check via requestUserMedia since denied is never reported.
   */
  async requestCam(): Promise<PermissionStateCombined> {
    if (this._cameraAccess() === 'granted') {
      return 'granted';
    }
    if (isTauri) {
      return this.requestNativeCam();
    }
    if (this._cameraAccess() === 'denied') {
      if (isFirefoxBrowser()) {
        const success = await this.requestUserMedia('video');
        if (success) {
          this.updateCameraAccess('granted');
          return 'granted';
        }
      }
      // Already denied — re-show the tutorial since the browser won't re-prompt
      this._showPermissionTutorial.set(true);
      return 'denied';
    }
    const success = await this.requestUserMedia('video');
    if (isFirefoxBrowser() && !success) {
      this.updateCameraAccess('denied');
    }
    if (success) {
      // We got a MediaStream, access must be "granted". Update the signal
      // explicitly: on Firefox/Linux the Permissions API onchange does not fire
      // when the user picks a non-persistent "Allow" (e.g. without ticking
      // "Remember this decision"), so the signal would otherwise stay at
      // 'prompt' and gate the UI off even though access is working.
      this.updateCameraAccess('granted');
      return 'granted';
    } else {
      // Access could be denied or "prompt".
      return this._cameraAccess();
    }
  }

  /**
   * Prompt the user for Microphone permissions.
   * The reactive listeners (onchange / poll) update the signal automatically.
   * On Firefox, we additionally check via requestUserMedia since denied is never reported.
   */
  async requestMic(): Promise<PermissionStateCombined> {
    if (this._microphoneAccess() === 'granted') {
      return 'granted';
    }
    if (isTauri) {
      return this.requestNativeMic();
    }

    if (this._microphoneAccess() === 'denied') {
      // Firefox has no denied state, so we need to probe via requestUserMedia to check if access
      // was granted. Technically, state should change after user interaction, but there might be a delay,
      // so it's returned hardcoded and immediately.
      if (isFirefoxBrowser()) {
        const success = await this.requestUserMedia('audio');
        if (success) {
          this.updateMicrophoneAccess('granted');
          return 'granted';
        }
      }
      // Already denied — re-show the tutorial since the browser won't re-prompt
      this._showPermissionTutorial.set(true);
      return 'denied';
    }
    const success = await this.requestUserMedia('audio');
    if (isFirefoxBrowser() && !success) {
      this.updateMicrophoneAccess('denied').catch(error);
    }
    if (success) {
      // We got a MediaStream, access must be "granted". Update the signal
      // explicitly: on Firefox/Linux the Permissions API onchange does not fire
      // when the user picks a non-persistent "Allow" (e.g. without ticking
      // "Remember this decision"), so the signal would otherwise stay at
      // 'prompt' and gate the mic UI off even though audio is working.
      this.updateMicrophoneAccess('granted').catch(error);
      return 'granted';
    } else {
      // Access could be denied or "prompt".
      return this._microphoneAccess();
    }
  }
}
